import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useContent } from '@/lib/content';
import { resolveMediaSrc } from '@/lib/api';
import { MaskedLine } from '@/components/Reveal';
import { scrollToId } from '@/lib/scroll';
import { trackEvent } from '@/lib/analytics';

export const Hero = () => {
  const { settings } = useContent();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[560px] overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -bottom-24">
        <img
          src={resolveMediaSrc(settings.heroImage)}
          alt="Luxury Indian wedding decor with gold florals by Events by AJ, event planner in Hyderabad"
          className="w-full h-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full max-w-7xl mx-auto px-5 sm:px-8 flex flex-col justify-end pb-24 sm:pb-28">
        <MaskedLine delay={0.5}>
          <p className="caption-label mb-6">Premium Event Management — Hyderabad</p>
        </MaskedLine>
        <h1 className="font-serif font-light tracking-tighter leading-[1.05] sm:leading-[0.95] text-cream text-5xl sm:text-6xl md:text-7xl lg:text-8xl">
          <MaskedLine delay={0.7}>We Create Experiences</MaskedLine>
          <MaskedLine delay={0.85}>
            You <span className="italic text-champagne">Cherish Forever.</span>
          </MaskedLine>
        </h1>
        <MaskedLine delay={1.05}>
          <p className="mt-6 max-w-xl text-base md:text-lg text-white/70 font-light tracking-wide leading-relaxed">
            From intimate celebrations to grand occasions, Events by AJ brings together creativity,
            planning and flawless execution — all under one roof.
          </p>
        </MaskedLine>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.8 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            data-testid="hero-enquire-btn"
            onClick={() => { trackEvent('enquire_click', { location: 'hero' }); scrollToId('#enquiry'); }}
            className="btn-gold"
          >
            Enquire Now
          </button>
          <button
            data-testid="hero-view-work-btn"
            onClick={() => { trackEvent('view_work_click', { location: 'hero' }); navigate('/portfolio'); }}
            className="btn-outline"
          >
            View Our Work
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        onClick={() => scrollToId('#about')}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-white/50 hover:text-champagne transition-colors"
        aria-label="Scroll down"
      >
        <ChevronDown size={26} className="animate-scroll-pulse" />
      </motion.button>
    </section>
  );
};
